import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Settings2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { formatMoney } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill, type Tone } from "@/components/ui/StatusPill";
import { Modal } from "@/components/ui/Modal";
import { toast } from "@/components/ui/toast";
import { ErrorState, Skeleton } from "@/components/ui/states";
import { ImagesPanel } from "./ImagesPanel";
import { SpecsPanel } from "./SpecsPanel";
import { VariantsPanel } from "./VariantsPanel";
import {
  archiveProduct,
  deleteProduct,
  fetchProductDetail,
  fetchTaxonomy,
  restoreProduct,
  updateProduct,
  updateVariant,
  type ProductDraft,
  type Variant,
} from "./productApi";

const STOCK_TONE: Record<string, Tone> = {
  ok: "ok",
  low: "warn",
  out_of_stock: "crit",
};

export function ProductDetailPage() {
  const { id = "" } = useParams();
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const detail = useQuery({ queryKey: ["product", id], queryFn: () => fetchProductDetail(id) });
  const taxonomy = useQuery({
    queryKey: ["taxonomy", org?.organizationId],
    queryFn: () => fetchTaxonomy(org!.organizationId),
    enabled: Boolean(org),
  });

  const [draft, setDraft] = useState<ProductDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Variant | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [quitando, setQuitando] = useState(false);

  // El borrador se inicializa una vez con lo que hay en la base. A partir de
  // ahi manda el formulario: si se resincronizara en cada refetch, cualquier
  // recarga en segundo plano borraria lo que el usuario esta escribiendo.
  useEffect(() => {
    if (detail.data && !draft) {
      const p = detail.data.product;
      setDraft({
        name: p.name,
        slug: p.slug,
        kind: p.kind,
        short_description: p.short_description,
        description: p.description,
        category_id: p.category_id,
        brand_id: p.brand_id,
        active: p.active,
        featured: p.featured,
        seo_title: p.seo_title,
        seo_description: p.seo_description,
      });
    }
  }, [detail.data, draft]);

  if (detail.isPending || !draft) return <DetailSkeleton />;
  if (detail.isError) {
    return <ErrorState message={friendlyError(detail.error)} onRetry={() => void detail.refetch()} />;
  }

  const { product, variants, options, images, stockByVariant, vendido } = detail.data;
  const currency = org?.currency ?? "PEN";

  const esServicio = draft.kind === "service";

  // Si la tienda solo admite un tipo no hay eleccion que ofrecer, y un
  // desplegable de una sola opcion es ruido que ademas sugiere que hay algo
  // que decidir. El tipo del producto se sigue respetando: solo desaparece el
  // control.
  const tiposPermitidos = org?.catalogKinds ?? ["physical"];
  const puedeElegirTipo = tiposPermitidos.length > 1;

  // El dominio sale de la organizacion activa. Estuvo escrito a mano como
  // «guauchis.pe» y eso, en un panel multi-tienda, le ensena a cada cliente el
  // dominio de otro. Mientras no haya dominio propio no se inventa ninguno:
  // se habla de «tu tienda», que es cierto siempre.
  const dominio = org?.primaryDomain ?? null;

  const original: ProductDraft = {
    name: product.name,
    slug: product.slug,
    kind: product.kind,
    short_description: product.short_description,
    description: product.description,
    category_id: product.category_id,
    brand_id: product.brand_id,
    active: product.active,
    featured: product.featured,
    seo_title: product.seo_title,
    seo_description: product.seo_description,
  };
  const dirty = (Object.keys(original) as (keyof ProductDraft)[]).some(
    (k) => (draft[k] ?? null) !== (original[k] ?? null),
  );

  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      await updateProduct(id, draft);
      toast.ok("Producto guardado");
      await qc.invalidateQueries({ queryKey: ["product", id] });
      await qc.invalidateQueries({ queryKey: ["products"] });
    } catch (e) {
      toast.error(friendlyError(e));
    }
    setSaving(false);
  }

  /**
   * Un producto vendido se archiva; uno que nunca se vendio se borra.
   *
   * La decision la toma la base, no esta funcion: order_items referencia al
   * producto con ON DELETE RESTRICT. Si `vendido` se quedara corto por lo que
   * sea, el borrado falla y el aviso lo explica, en vez de dejar datos a medias.
   */
  async function quitar() {
    setQuitando(true);
    try {
      if (vendido) {
        await archiveProduct(id);
        toast.ok("Producto archivado. Sus ventas siguen intactas.");
      } else {
        await deleteProduct(id);
        toast.ok("Producto eliminado");
      }
      await qc.invalidateQueries({ queryKey: ["products"] });
      navigate("/catalogo");
    } catch (e) {
      toast.error(friendlyError(e));
      setQuitando(false);
      setConfirmando(false);
    }
  }

  async function restaurar() {
    try {
      await restoreProduct(id);
      toast.ok("Producto restaurado. Sigue oculto hasta que lo publiques.");
      await qc.invalidateQueries({ queryKey: ["product", id] });
      await qc.invalidateQueries({ queryKey: ["products"] });
    } catch (e) {
      toast.error(friendlyError(e));
    }
  }

  return (
    <div className="flex flex-col gap-3 pb-16">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Link
            to="/catalogo"
            className="mt-0.5 grid size-7 place-items-center rounded-md text-ink-2 hover:bg-sunk"
            aria-label="Volver al catálogo"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <div>
            <h1 className="text-head font-semibold tracking-tight">{product.name}</h1>
            <p className="tnum text-xs text-ink-3">
              {variants.length} variante{variants.length === 1 ? "" : "s"} · /{product.slug}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {product.archived_at ? (
            <StatusPill tone="warn">Archivado</StatusPill>
          ) : (
            <StatusPill tone={product.active ? "ok" : "neutral"}>
              {product.active ? "Publicado" : "Oculto"}
            </StatusPill>
          )}
          {product.featured && !product.archived_at && (
            <StatusPill tone="accent" dot={false}>
              Destacado
            </StatusPill>
          )}

          {product.archived_at ? (
            <Button size="sm" variant="secondary" onClick={() => void restaurar()}>
              Restaurar
            </Button>
          ) : (
            <Button size="sm" variant="danger" onClick={() => setConfirmando(true)}>
              {vendido ? "Archivar" : "Eliminar"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <Panel title="Información">
            <div className="flex flex-col gap-3.5">
              <Field label="Nombre">
                <input
                  value={draft.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                />
              </Field>

              {/* El tipo va arriba y no escondido en un panel tecnico: cambia
                  como se vende la ficha entera —si reserva stock, si puede
                  agotarse, si el pedido caduca sin pagar— y quien carga el
                  catalogo lo tiene claro desde el primer momento. */}
              {puedeElegirTipo && (
                <Field
                  label="Tipo"
                  hint={
                    esServicio
                      ? "No lleva inventario: nunca aparece agotado y su pedido no caduca por falta de pago."
                      : "Lleva inventario: al venderse reserva stock y puede agotarse."
                  }
                >
                  <select
                    value={draft.kind}
                    onChange={(e) => set("kind", e.target.value)}
                    className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                  >
                    {tiposPermitidos.includes("physical") && (
                      <option value="physical">Producto</option>
                    )}
                    {tiposPermitidos.includes("service") && (
                      <option value="service">Servicio</option>
                    )}
                  </select>
                </Field>
              )}

              <Field
                label="Enlace en la tienda"
                hint={dominio ? `${dominio}/${draft.slug}` : `/${draft.slug}`}
              >
                <input
                  value={draft.slug}
                  onChange={(e) =>
                    set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))
                  }
                  className="tnum w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                />
              </Field>

              <Field label="Descripción corta" hint="Es lo que se lee bajo el nombre en el catálogo.">
                <textarea
                  rows={2}
                  value={draft.short_description ?? ""}
                  onChange={(e) => set("short_description", e.target.value || null)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                />
              </Field>

              <Field label="Descripción">
                <textarea
                  rows={5}
                  value={draft.description ?? ""}
                  onChange={(e) => set("description", e.target.value || null)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                />
              </Field>

              {/* Los campos propios de cada rubro viven aqui, no en un panel
                  aparte: para quien carga el catalogo son informacion del
                  producto como cualquier otra, no una seccion tecnica. */}
              <SpecsPanel
                bare
                organizationId={product.organization_id}
                productId={product.id}
                categoryId={draft.category_id}
              />

              {/* Los atributos NO se crean desde aqui: son del catalogo
                  entero y su numero depende del plan contratado. */}
              <Link
                to="/configuracion/atributos"
                className="flex items-center gap-1.5 self-start text-meta text-accent hover:underline"
              >
                <Settings2 className="size-3.5" />
                Administrar atributos
              </Link>
            </div>
          </Panel>

          <VariantsPanel
            productId={product.id}
            organizationId={product.organization_id}
            categoryId={draft.category_id}
            options={options}
            currency={currency}
          />

          <Panel
            title="Variantes"
            bleed
            actions={<span className="text-xs text-ink-3">{options.map((o) => o.name).join(" · ") || "Sin opciones"}</span>}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-ui">
                <thead>
                  <tr>
                    {(esServicio
                      ? ["Variante", "SKU", "Precio", ""]
                      : ["Variante", "SKU", "Precio", "Stock", ""]
                    ).map((h, i) => (
                      <th
                        key={h || i}
                        className={cn(
                          "border-b border-line bg-sunk px-4 py-2.5 font-mono text-mark font-medium uppercase tracking-[0.1em] text-ink-3",
                          h === "Precio" || h === "Stock" ? "text-right" : "text-left",
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => {
                    const stock = stockByVariant[v.id];
                    return (
                      <tr key={v.id} className="border-b border-line-2 last:border-0">
                        <td className="px-4 py-3">
                          <span className="font-medium">{v.name}</span>
                          {!v.active && (
                            <span className="ml-2 text-xs text-ink-3">(desactivada)</span>
                          )}
                        </td>
                        <td className="tnum px-4 py-3 text-xs text-ink-3">{v.sku}</td>
                        <td className="tnum px-4 py-3 text-right font-semibold">
                          {formatMoney(v.sale_price, currency, { withSymbol: false })}
                          {v.compare_at_price && (
                            <span className="block text-xs font-normal text-ink-3 line-through">
                              {formatMoney(v.compare_at_price, currency, { withSymbol: false })}
                            </span>
                          )}
                        </td>
                        {/* Un servicio no tiene fila de inventario. Mostrar la
                            columna diria «sin almacén» en todas las variantes,
                            que se lee como un error de configuracion cuando es
                            exactamente lo correcto. */}
                        {!esServicio && (
                          <td className="px-4 py-3 text-right">
                            {stock ? (
                              <StatusPill tone={STOCK_TONE[stock.status] ?? "neutral"}>
                                {stock.available}
                              </StatusPill>
                            ) : (
                              <span className="text-xs text-ink-3">sin almacén</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant="secondary" onClick={() => setEditing(v)}>
                            Editar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="border-t border-line-2 px-4 py-2.5 text-xs text-ink-3">
              {esServicio
                ? "Un servicio no lleva inventario: sus variantes sirven para tramos, duraciones o alcances, y nunca se agotan."
                : "El stock se ajusta desde Inventario, no aquí: cada cambio necesita motivo y deja movimiento."}
            </p>
          </Panel>
        </div>

        <div className="flex flex-col gap-3">
          <Panel title="Publicación">
            <div className="flex flex-col gap-3">
              <Toggle
                label="Visible en la tienda"
                hint={dominio ? `Se publica en ${dominio}` : "Se publica en tu tienda"}
                value={draft.active}
                onChange={(v) => set("active", v)}
              />
              <Toggle
                label="Destacado"
                hint="Aparece en la portada"
                value={draft.featured}
                onChange={(v) => set("featured", v)}
              />
            </div>
          </Panel>

          <Panel title="Clasificación">
            <div className="flex flex-col gap-3.5">
              <Field label="Categoría">
                <select
                  value={draft.category_id ?? ""}
                  onChange={(e) => set("category_id", e.target.value || null)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                >
                  <option value="">Sin categoría</option>
                  {(taxonomy.data?.categories ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Marca">
                <select
                  value={draft.brand_id ?? ""}
                  onChange={(e) => set("brand_id", e.target.value || null)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                >
                  <option value="">Sin marca</option>
                  {(taxonomy.data?.brands ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Panel>

          <ImagesPanel
            organizationId={product.organization_id}
            productId={product.id}
            images={images}
            onChanged={() => void detail.refetch()}
          />

          <Panel title="SEO">
            <div className="flex flex-col gap-3.5">
              <Field label="Título" hint="Lo que sale en Google.">
                <input
                  value={draft.seo_title ?? ""}
                  onChange={(e) => set("seo_title", e.target.value || null)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                />
              </Field>
              <Field label="Descripción">
                <textarea
                  rows={3}
                  value={draft.seo_description ?? ""}
                  onChange={(e) => set("seo_description", e.target.value || null)}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
                />
              </Field>
            </div>
          </Panel>
        </div>
      </div>

      {/* Barra de guardado fija: en un formulario largo, perder el trabajo es
          el fallo mas caro que puede tener la pantalla. */}
      {dirty && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-raised px-4 py-3 md:left-[216px]">
          <div className="flex items-center justify-between gap-3">
            <span className="text-ui text-ink-2">Tienes cambios sin guardar</span>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setDraft(original)}>
                Descartar
              </Button>
              <Button size="sm" variant="primary" loading={saving} onClick={() => void save()}>
                Guardar cambios
              </Button>
            </div>
          </div>
        </div>
      )}

      <VariantDialog
        variant={editing}
        currency={currency}
        organizationId={product.organization_id}
        categoryId={draft.category_id}
        onClose={() => setEditing(null)}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ["product", id] });
          setEditing(null);
        }}
      />

      {/* Se dice exactamente que va a pasar y por que, en vez del «esta accion
          no se puede deshacer» de siempre, que no distingue entre las dos
          cosas muy distintas que hace este boton. */}
      <Modal
        open={confirmando}
        onClose={() => setConfirmando(false)}
        tone="danger"
        title={vendido ? `Archivar ${product.name}` : `Eliminar ${product.name}`}
        description={
          vendido
            ? "Este producto ya figura en pedidos, así que no se borra: sale del catálogo y de la tienda, y sus ventas quedan intactas. Puedes restaurarlo cuando quieras."
            : "Nunca se ha vendido, así que se borra de verdad, junto con sus variantes, imágenes y ficha técnica. Esto no se deshace."
        }
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConfirmando(false)}
              disabled={quitando}
            >
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => void quitar()} loading={quitando}>
              {vendido ? "Archivar" : "Eliminar"}
            </Button>
          </>
        }
      />
    </div>
  );
}

/* ------------------------------------------------------------------ piezas */

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-meta font-medium">{label}</span>
      {children}
      {hint && <span className="text-xs text-ink-3">{hint}</span>}
    </label>
  );
}

function Toggle({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>
        <span className="block text-ui font-medium">{label}</span>
        <span className="block text-xs text-ink-3">{hint}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        onClick={() => onChange(!value)}
        className={cn(
          "relative h-5 w-9 shrink-0 rounded-full transition-colors",
          value ? "bg-accent" : "bg-line",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 size-4 rounded-full bg-surface transition-transform",
            value ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}

function VariantDialog({
  variant,
  currency,
  organizationId,
  categoryId,
  onClose,
  onSaved,
}: {
  variant: Variant | null;
  currency: string;
  organizationId: string;
  categoryId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState("");
  const [compare, setCompare] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!variant) return;
    setSku(variant.sku);
    setPrice(variant.sale_price);
    setCompare(variant.compare_at_price ?? "");
    setActive(variant.active);
  }, [variant]);

  if (!variant) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Variante ${variant.name}`}
      description="El precio tachado debe ser mayor que el de venta: la base rechaza lo contrario."
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await updateVariant(variant.id, {
                  sku: sku.trim(),
                  sale_price: price,
                  compare_at_price: compare.trim() || null,
                  active,
                });
                toast.ok("Variante actualizada");
                onSaved();
              } catch (e) {
                toast.error(friendlyError(e));
              }
              setBusy(false);
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="SKU">
          <input
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            className="tnum w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
          />
        </Field>
        <Field label="Precio de venta">
          <span className="flex">
            <span className="flex items-center rounded-l-md border border-r-0 border-line bg-sunk px-2.5 font-mono text-meta text-ink-3">
              {currency === "PEN" ? "S/" : currency}
            </span>
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="decimal"
              className="tnum w-full rounded-r-md border border-line bg-surface px-3 py-2 text-right text-ui"
            />
          </span>
        </Field>
        <Field label="Precio tachado" hint="Opcional. Se muestra como precio anterior.">
          <span className="flex">
            <span className="flex items-center rounded-l-md border border-r-0 border-line bg-sunk px-2.5 font-mono text-meta text-ink-3">
              {currency === "PEN" ? "S/" : currency}
            </span>
            <input
              value={compare}
              onChange={(e) => setCompare(e.target.value)}
              inputMode="decimal"
              className="tnum w-full rounded-r-md border border-line bg-surface px-3 py-2 text-right text-ui"
            />
          </span>
        </Field>
        <Toggle
          label="Variante a la venta"
          hint="Si se desactiva, deja de poder comprarse"
          value={active}
          onChange={setActive}
        />

        {/* Los campos de nivel variante se guardan al momento, no con el boton
            de abajo: son filas independientes en product_specs. */}
        <SpecsPanel
          bare
          organizationId={organizationId}
          productId={variant.product_id}
          categoryId={categoryId}
          variantId={variant.id}
        />
      </div>
    </Modal>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-6 w-56" />
      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Skeleton className="h-96 rounded-lg" />
        <Skeleton className="h-96 rounded-lg" />
      </div>
    </div>
  );
}
